/**
 * Per-field help for the model runtime policy form (the (?) popover next to each label).
 * The policy editor sections read it with `help={RUNTIME_POLICY_FIELD_HELP.<field>}`.
 */
export const RUNTIME_POLICY_FIELD_HELP = {
  name: 'The name people see when they pick this policy. Make it say what the policy is for, for example "Interactive default".',
  slug: 'A short identifier in lowercase letters and hyphens. Other settings refer to the policy by it, so it cannot be changed after the policy is created.',
  status:
    'Draft policies are being prepared, active policies can be chosen, and archived policies are retired. Archiving a policy moves the work that used it to the default policy.',
  description: 'A sentence for the next person: what kind of work this policy is meant for and why it is set as it is.',
  purpose:
    'The kind of work the policy is meant for. When work does not name a policy, the resolver prefers the active policy whose purpose matches.',
  riskTier:
    'How much damage a run under this policy could do. Privileged policies are never picked automatically and only a superadmin can choose them by name.',
  latencyClass:
    'How quickly an answer is needed: real time for a person waiting, interactive for chat, batch for work nobody waits on.',
  costTier:
    'How expensive the models in this policy are meant to be. The resolver uses it to keep cheap work off costly models.',
  permissionTier: 'The role level a run needs before it may use this policy: observer, contributor or operator.',
  tags: 'Free labels that help people find the policy in the catalogue. They do not change how the policy runs.',
  maxTurns: 'The most back-and-forth rounds with the model one run may take before it is stopped.',
  maxToolCalls: 'The most tool calls one run may make. 0 means no limit.',
  maxInputTokens: 'The largest prompt, in tokens, a run may send to the model. Leave empty for no limit.',
  maxOutputTokens: 'The longest answer, in tokens, the model may produce in one turn.',
  maxConcurrentRequests:
    'The most of this policy’s calls that run on one model server at the same time. It only lowers the model’s own limit, never raises it: a server that runs four at once still runs four. Use it to keep room for other work, for example a batch policy at 2. Leave empty to use the model’s limit alone.',
  wallClockTimeoutMs:
    'How long a whole run may last, in milliseconds, retries included. The run is stopped when it passes this.',
  idleTimeoutMs:
    'How long a run may sit with no progress, in milliseconds, before it is stopped. Leave empty for none.',
  retryMax: 'How many times a failed provider call is tried again before the run fails.',
  retryBackoffMs: 'The wait before the first retry, in milliseconds.',
  retryBackoffMultiplier: 'How much longer each further retry waits. 2 doubles the wait every time.',
  providerRequestTimeoutMs:
    'How long one provider call may take, in milliseconds, before it counts as failed and is retried.',
  spendCapUsd:
    'The most one run may spend, in US dollars. Leave empty for no cap. A cap above 25 dollars needs a second approver.',
  retryJitter: 'Adds a random amount to each retry wait so many runs do not all retry at the same moment.',
  permissionMode:
    'What the model may do on its own. Read only blocks writes; bypass permissions lifts every restriction and needs a second approver.',
  sessionMode:
    'Whether the provider keeps the conversation between turns (reuse session) or each turn starts clean (stateless).',
  allowedProviderTools: 'The provider-side tools the model may use. Leave empty to allow every provider tool.',
  streamingEnabled:
    'Sends the answer back as it is written instead of all at once. Turn it off only if the provider streams badly.',
  promptCachingEnabled:
    'Lets the provider reuse the unchanged start of a prompt, which makes repeated runs faster and cheaper.',
  maxSystemPromptChars:
    'The longest system prompt, in characters, that may be sent. Longer prompts are cut. Leave empty for no limit.',
  providerExtensions:
    'Provider-specific options as JSON, keyed by provider. Use it for a setting this form has no field for.',
  fallback: 'The policy that runs when every model in this one fails. Pick "No fallback" to let the run fail.',
  thinking: 'Whether the model reasons before it answers. Model default leaves the choice to the model.',
} as const satisfies Record<string, string>;
